/** True only when is_default is explicitly 1 or true (avoids "0" string being truthy) */
export function isDefaultAddress(addr: { is_default?: unknown }): boolean {
  return addr.is_default === true || addr.is_default === 1 || addr.is_default === '1';
}

/**
 * Remove trailing US country labels so "…, CA 95762, United States" parses as city/state/ZIP.
 */
export function stripUsCountrySuffix(address: string): string {
  return (address || '')
    .replace(/\s*,\s*(United States of America|United States|USA|U\.S\.A\.|U\.S\.)\s*$/i, '')
    .replace(/\s+(United States of America|United States|USA|U\.S\.A\.|U\.S\.)\s*$/i, '')
    .trim()
    .replace(/,\s*$/, '')
    .trim();
}

const US_STATE_ABBREV_BY_NAME: Record<string, string> = {
  alabama: 'AL', alaska: 'AK', arizona: 'AZ', arkansas: 'AR',
  california: 'CA', colorado: 'CO', connecticut: 'CT', delaware: 'DE',
  florida: 'FL', georgia: 'GA', hawaii: 'HI', idaho: 'ID',
  illinois: 'IL', indiana: 'IN', iowa: 'IA', kansas: 'KS',
  kentucky: 'KY', louisiana: 'LA', maine: 'ME', maryland: 'MD',
  massachusetts: 'MA', michigan: 'MI', minnesota: 'MN', mississippi: 'MS',
  missouri: 'MO', montana: 'MT', nebraska: 'NE', nevada: 'NV',
  'new hampshire': 'NH', 'new jersey': 'NJ', 'new mexico': 'NM', 'new york': 'NY',
  'north carolina': 'NC', 'north dakota': 'ND', ohio: 'OH', oklahoma: 'OK',
  oregon: 'OR', pennsylvania: 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
  'south dakota': 'SD', tennessee: 'TN', texas: 'TX', utah: 'UT',
  vermont: 'VT', virginia: 'VA', washington: 'WA', 'west virginia': 'WV',
  wisconsin: 'WI', wyoming: 'WY', 'district of columbia': 'DC',
};

/** Normalize "CA" or "California" → "CA"; empty if unknown. */
export function normalizeUsStateAbbrev(raw: string): string {
  const t = (raw || '').trim();
  if (/^[A-Za-z]{2}$/.test(t)) return t.toUpperCase();
  return US_STATE_ABBREV_BY_NAME[t.toLowerCase()] || '';
}

/**
 * Normalize address fields for duplicate comparison.
 * Trims, lowercases, collapses whitespace, and extracts 5-digit ZIP.
 */
export function normalizeAddressKey(line1: string, city: string, state: string, postal_code: string): string {
  const norm = (s: string) =>
    (s || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .replace(/[,#.]/g, '');
  const zip = (postal_code || '').replace(/\D/g, '').slice(0, 5);
  return [norm(line1), norm(city), norm(state), zip].join('|');
}

type AddressLike = {
  id?: number;
  line1?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  address?: string;
};

/**
 * Parse 2-letter US state from a formatted address string (e.g. "Austin, TX 78701" or "123 Main St, City, TX").
 * Returns empty string if not found.
 */
/** 5-digit ZIP from structured fields or formatted address */
export function extractPostalCodeFromAddress(
  addressData?: { postal_code?: string } | null,
  formattedAddress?: string
): string {
  const fromData = (addressData?.postal_code || '').replace(/\D/g, '').slice(0, 5);
  if (fromData.length === 5) return fromData;
  const m = stripUsCountrySuffix(formattedAddress || '').match(/\b(\d{5})(?:-\d{4})?\b/);
  return m?.[1] || '';
}

/**
 * True when address has street, city, 2-letter state, and ZIP — not city/area only (e.g. "Use my location").
 */
export function isCompleteDeliveryAddress(
  addressData?: { line1?: string; city?: string; state?: string; postal_code?: string; formatted?: string } | null,
  formattedAddress?: string
): boolean {
  const line1 = (addressData?.line1 || '').trim();
  const city = (addressData?.city || '').trim();
  const state = normalizeUsStateAbbrev(addressData?.state || '');
  const formatted = stripUsCountrySuffix(formattedAddress || addressData?.formatted || '');
  const postal = extractPostalCodeFromAddress(addressData, formatted);

  if (!line1 || !city || !state || postal.length !== 5) return false;

  const hasStreetNumber = /\d/.test(line1);
  const line1LooksLikeCityOnly =
    line1.toLowerCase() === city.toLowerCase() ||
    (!hasStreetNumber && line1.split(/\s+/).filter(Boolean).length < 2);

  if (line1LooksLikeCityOnly) {
    const parts = formatted.split(',').map((p) => p.trim()).filter(Boolean);
    if (parts.length < 3 || !/\b\d{5}\b/.test(formatted)) return false;
  }

  if (!hasStreetNumber && !/\b\d{5}\b/.test(formatted)) return false;

  return true;
}

export function isCompleteSavedAddress(addr: AddressLike): boolean {
  const formatted = stripUsCountrySuffix(addr.address || '');
  const line1 = (addr.line1 || parseStreetFromAddress(formatted)).trim();
  const city = (addr.city || parseCityFromAddress(formatted)).trim();
  const state = normalizeUsStateAbbrev(addr.state || parseStateFromAddress(formatted));
  return isCompleteDeliveryAddress(
    {
      line1,
      city,
      state,
      postal_code: addr.postal_code || extractPostalCodeFromAddress(null, formatted),
      formatted,
    },
    formatted
  );
}

export function parseStateFromAddress(address: string): string {
  if (!address || typeof address !== 'string') return '';
  const s = stripUsCountrySuffix(address);
  // Full state name before ZIP first (avoids ", El" matching from "El Dorado Hills")
  const mFull = s.match(/,\s*([A-Za-z][A-Za-z\s]+?)\s+(\d{5}(?:-\d{4})?)\s*$/);
  if (mFull?.[1]) {
    const abbr = normalizeUsStateAbbrev(mFull[1].trim());
    if (abbr) return abbr;
  }
  // ", CA 95762" or ", CA" at end / before comma
  const m = s.match(/,\s*([A-Za-z]{2})(?:\s+\d{5}(?:-\d{4})?)?(?:\s*,|\s*$)/);
  if (m?.[1]) return m[1].toUpperCase();
  // Fallback: look for 2-letter token before 5-digit zip
  const m2 = s.match(/\b([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)\b/);
  if (m2?.[1]) return m2[1].toUpperCase();
  return '';
}

/**
 * Parse street line (first part before comma) from a full address string.
 * e.g. "123 Main St, Sacramento, CA 95814" -> "123 Main St"
 */
export function parseStreetFromAddress(address: string): string {
  if (!address || typeof address !== 'string') return '';
  const cleaned = stripUsCountrySuffix(address);
  const idx = cleaned.indexOf(',');
  return idx === -1 ? cleaned.trim() : cleaned.slice(0, idx).trim();
}

/**
 * Display label: street number + street name only (no city, state, zip, country).
 * Prefer structured line1; otherwise first segment before comma of the formatted string.
 */
export function addressStreetLineOnly(
  addressData: { line1?: string } | null | undefined,
  fullFormatted: string
): string {
  let line = (addressData?.line1 || '').trim();
  if (line.includes(',')) line = parseStreetFromAddress(line);
  if (line) return line;
  return parseStreetFromAddress(fullFormatted || '');
}

/**
 * Parse city from a full address string (second comma-separated part, without state/zip).
 * e.g. "123 Main St, Sacramento, CA 95814" -> "Sacramento"
 * Ignores trailing ", United States".
 */
export function parseCityFromAddress(address: string): string {
  if (!address || typeof address !== 'string') return '';
  const parts = stripUsCountrySuffix(address)
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length >= 2) return parts[1];
  return '';
}

/**
 * Check if an address already exists in the list (excluding the one being edited).
 */
export function isDuplicateAddress(
  existing: AddressLike[],
  line1: string,
  city: string,
  state: string,
  postal_code: string,
  excludeId?: number | null
): boolean {
  const key = normalizeAddressKey(line1, city, state, postal_code);
  return existing.some((addr) => {
    if (excludeId != null && addr.id === excludeId) return false;
    const a = (addr.line1 || (addr.address || '').split(',')[0] || '').trim();
    const c = addr.city || '';
    const s = addr.state || '';
    const p = addr.postal_code || '';
    return normalizeAddressKey(a, c, s, p) === key;
  });
}
